import { Box, Typography } from '@mui/material';
import { formatDateToBR } from '../../../utils/formatDateToBR';
import { formatTime } from '../../../utils/formatTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { mergeTheme } from '../types';
import type { ThemeCustomization } from '../types';

const EVENT_WITHOUT_DATE_DISPLAY = 'ba1ba056-fbcd-46cd-9569-95d04e4b6932';

interface EventDescriptionProps {
  eventId?: string;
  title: string;
  startDate: string;
  endDate: string;
  startTime?: string | null;
  endTime?: string | null;
  description: string;
  tagline?: string;
  themeCustomization?: ThemeCustomization;
}

export function EventDescription({
  eventId,
  title,
  startDate,
  endDate,
  startTime,
  endTime,
  description,
  tagline,
  themeCustomization,
}: EventDescriptionProps) {
  const mergedTheme = mergeTheme(themeCustomization);
  const textColor = mergedTheme.colors?.textColor || '#222222';
  const textSecondaryColor = mergedTheme.colors?.textSecondaryColor || '#424242';
  const primaryColor = mergedTheme.colors?.primaryColor || '#0B6DCE';
  const shouldHideDate = Boolean(eventId) && eventId === EVENT_WITHOUT_DATE_DISPLAY;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h4"
        fontWeight={500}
        color="text.primary"
        gutterBottom
        sx={{
          mb: 2,
          fontSize: '2.5rem',
          color: textColor, // Texto principal (título)
          lineHeight: 1.3,
          fontFamily: mergedTheme.typography?.fontFamily,
        }}
      >
        {title}
      </Typography>

      {!shouldHideDate && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <CalendarTodayIcon fontSize="small" sx={{ color: primaryColor }} />
          <Typography
            variant="body2"
            sx={{
              color: primaryColor,
              fontSize: '1.125rem',
              fontWeight: 500,
              fontFamily: mergedTheme.typography?.fontFamily,
            }}
          >
            {formatDateToBR(startDate)}
            {startTime && ` às ${formatTime(startTime)}`} -{' '}
            {formatDateToBR(endDate)}
            {endTime && ` às ${formatTime(endTime)}`}
          </Typography>
        </Box>
      )}

      {tagline && (
        <Typography
          variant="h6"
          sx={{
            mb: 2,
            fontStyle: 'italic',
            color: textSecondaryColor, // Texto secundário (subtítulo)
            fontSize: '1.125rem',
            fontWeight: 500,
            fontFamily: mergedTheme.typography?.fontFamily,
          }}
        >
          {tagline}
        </Typography>
      )}

      <Box
        sx={{
          '& p': {
            marginBottom: 2,
            textAlign: 'justify',
            lineHeight: 1.8,
            color: textColor, // Texto principal
            fontSize: '0.9375rem',
            fontFamily: mergedTheme.typography?.fontFamily,
          },
        }}
        dangerouslySetInnerHTML={{ __html: description }}
      />
    </Box>
  );
}

